import { Injectable } from '@angular/core';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonFunctionsService, DialogType } from './common-functions.service';
import { LogService } from './log.service';
import { AuthService } from './auth.service';
import {
  NotificationType,
  PushNotificationContainer,
  SilentActions,
  InboxNotification,
} from './definitions.service';
import { DataModelService } from './data-model.service';
import { Router } from '@angular/router';
import { UniversalDialogDialogData } from '../universal-dialog/universal-dialog';
import firebase from 'firebase/app';

@Injectable({
  providedIn: 'root',
})
export class MessagingService {
  currentMessage = new BehaviorSubject<PushNotificationContainer>(null);
  isRequestingToken = new BehaviorSubject<boolean>(false);
  unreadCount$: Observable<number> = of(0);

  // Tracks FCM message IDs that have already been saved to Firestore inbox.
  // Prevents duplicate entries when a background message is saved via the
  // BACKGROUND_MESSAGE_RECEIVED broadcast AND the user subsequently clicks the
  // OS notification (triggering a second NOTIFICATION_CLICK save attempt).
  private savedMessageIds = new Set<string>();

  constructor(
    private angularFireMessaging: AngularFireMessaging,
    private commonFunctions: CommonFunctionsService,
    private log: LogService,
    private authenticationService: AuthService,
    private dataModel: DataModelService,
    public router: Router
  ) {}

  /** Call once after login to wire up the unread-count observable. */
  public initInbox(userId: string) {
    this.unreadCount$ = this.dataModel
      .subscribeToInboxNotifications(userId)
      .pipe(map(notifications => notifications.filter(n => !n.read).length));
  }

  //Starts asking if user wants to receive push notification
  public requestPermission(force: boolean = false) {
    if (this.authenticationService.isLoggedIn) {
      this.requestNotificationPermission(
        this.authenticationService.userDetails.uid,
        force
      );
    } else {
      this.commonFunctions.showErrorToast('User nicht eingeloggt');
    }
  }

  receiveMessage() {
    // Foreground messages (app is open)
    this.angularFireMessaging.messages.subscribe(
      (payload: PushNotificationContainer) => {
        console.log('Message received. ', payload);
        this.currentMessage.next(payload);
        this.handleIncomingPayload(payload, false);
      }
    );

    // Service worker messages: only background message receipts remain here.
    // Notification clicks are handled via URL query params in NotificationInboxComponent.
    if (navigator.serviceWorker) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const msg = event.data;
        if (!msg || !msg.payload) return;

        if (msg.type === 'BACKGROUND_MESSAGE_RECEIVED') {
          // App was open in a background tab when the push arrived.
          // Save silently to Firestore so the inbox and badge update immediately
          // without any dialog or navigation interrupting the user.
          console.log('Background message received while app was open. ', msg.payload);
          const payload = msg.payload as PushNotificationContainer;
          this.currentMessage.next(payload);
          this.handleIncomingPayload(payload, false, false /* showDialog */);
        }
      });
    }
  }

  /**
   * Handles a received FCM payload: saves to inbox and optionally navigates.
   * @param navigateToInbox When true, navigate to /notifications instead of showing a dialog.
   * @param showDialog When false, suppress the in-app dialog (used for background-tab saves).
   */
  private handleIncomingPayload(
    payload: PushNotificationContainer,
    navigateToInbox: boolean,
    showDialog: boolean = true
  ) {
    const data = payload?.data || {};
    const messageType = parseInt(data.messageType, 10) || 0;

    // Silent actions never go to the inbox
    if (messageType === NotificationType.SilentAction) {
      this.handleSilentActions(parseInt(data.silentActionId, 10));
      return;
    }

    // Save to Firestore inbox — skip if we already saved this message ID
    // (prevents duplicates when BACKGROUND_MESSAGE_RECEIVED and the subsequent
    // URL-param path both try to save the same push).
    const userId = this.authenticationService.userId;
    const messageId = payload.fcmMessageId;
    if (userId && !(messageId && this.savedMessageIds.has(messageId))) {
      const entry: InboxNotification = {
        fcmMessageId: messageId || undefined,
        title: payload.notification?.title || data.messageTitle || 'Residenz App',
        body: payload.notification?.body || data.messageBody || '',
        messageType: messageType,
        messageTitle: data.messageTitle || '',
        messageBody: data.messageBody || '',
        senderUid: data.senderUid || '',
        senderName: data.senderName || '',
        route: data.route || '',
        timestamp: firebase.firestore.Timestamp.fromDate(new Date()),
        read: false,
      };
      this.dataModel.saveInboxNotification(userId, entry);
      if (messageId) {
        this.savedMessageIds.add(messageId);
        // Clean up after 10 minutes to avoid unbounded set growth
        setTimeout(() => this.savedMessageIds.delete(messageId), 10 * 60 * 1000);
      }
    }

    if (navigateToInbox) {
      this.router.navigate(['/notifications']);
      return;
    }

    if (showDialog) {
      this.displayNotification(messageType, data);
    }
  }

  //Shows a push notification dialog
  private displayNotification(messageType: number, data: { [key: string]: string }) {
    const title = data.messageTitle || '';
    const body = data.messageBody || '';

    if (messageType === NotificationType.Standard) {
      this.commonFunctions.openDialog(title, body);
    } else if (messageType === NotificationType.MessageFromAdmin) {
      this.commonFunctions.openDialog('Nachricht vom Admin', body);
    } else if (messageType === NotificationType.GeneralReminder) {
      this.commonFunctions.openDialog('Erinnerung', body);
    } else if (messageType === NotificationType.TestMessage) {
      this.commonFunctions.openDialog('\uD83E\uDDEA Test Notification', body);
    } else {
      this.commonFunctions.openDialog(title || 'Residenz App', body);
    }

    if (data.route) {
      this.router.navigate(['/' + data.route]);
    }
  }

  /**
   * Called by NotificationInboxComponent when the app opens via a notification
   * click URL (`/notifications?n=<encoded>`). Reconstructs the FCM payload from
   * the compact URL data and saves it to the Firestore inbox — unless already
   * saved in this session via BACKGROUND_MESSAGE_RECEIVED (deduplication via
   * savedMessageIds).
   */
  public processNotificationFromUrl(compact: {
    id: string; t: string; b: string;
    mt: string; mtl: string; mbl: string;
    uid: string; name: string; rt: string; said: string;
  }): void {
    const payload: PushNotificationContainer = {
      fcmMessageId: compact.id,
      from: '',
      priority: '',
      notification: { title: compact.t, body: compact.b },
      data: {
        messageType:    compact.mt,
        messageTitle:   compact.mtl,
        messageBody:    compact.mbl,
        senderUid:      compact.uid,
        senderName:     compact.name,
        route:          compact.rt,
        silentActionId: compact.said,
      },
    };
    this.handleIncomingPayload(payload, false, false);
  }

  //Handles silent actions without any user notification
  private handleSilentActions(silentActionId: number) {
    if (silentActionId === SilentActions.SendLogFilesToDev) {
      this.log
        .sendLogsToDev(this.authenticationService.userId)
        .then(
          () => console.log('Logs erfolgreich versandt'),
          (rejectMsg) => console.log('Logs nicht versandt: ' + rejectMsg)
        );
    }
    if (silentActionId === SilentActions.LogOut) {
      this.authenticationService.logout();
    }
    if (silentActionId === SilentActions.ClearLogFiles) {
      this.log.clearLogs();
    }
  }

  /**
   * Requests permission to retrieve web notifications
   * Source: https://github.com/angular/angularfire/blob/master/docs/messaging/messaging.md
   */
  public requestNotificationPermission(userId: string, force: boolean = false) {
    //already requested?
    var alreadyRequested = localStorage.getItem('notiTokenRequested');
    if (alreadyRequested == null || force) {
      this.commonFunctions
        .openDialog(
          'Benachrichtigungen',
          'Möchtest du Push-Benachrichtigungen erhalten?',
          DialogType.INFO,
          'Nein',
          'Ja'
        )
        .afterClosed()
        .subscribe((result: UniversalDialogDialogData) => {
          localStorage.setItem('notiTokenRequested', 'true');

          if (result.result == 1) {
            this.isRequestingToken.next(true);
            this.angularFireMessaging.requestToken.subscribe(
              (token) => {
                this.isRequestingToken.next(false);
                this.log.debug(
                  'Permission for receiving notifications granted. Sending token to server',
                  token,
                  CommonFunctionsService.name
                );
                this.commonFunctions.saveNotiTokenInUserCollection(
                  userId,
                  token
                );
              },
              (error) => {
                this.isRequestingToken.next(false);
                this.commonFunctions.showErrorToast(
                  'Registrierung nicht möglich'
                );
                this.log.error(
                  'Request for receiving notification token failed',
                  error,
                  CommonFunctionsService.name
                );
              }
            );
          }
        });
    }
  }
}
